import { useEffect, useRef } from 'react'
import type { Block } from '@/types/blocks'

/** Пауза в правке, после которой экран сохраняется сам. */
export const AUTO_SAVE_DELAY_MS = 700

interface Input {
  draft: Block | null
  /** Серверный блок выделения. */
  block: Block | null
  isDirty: boolean
  doSave: () => Promise<void>
  /** Автосохранение разрешено (тип блока, права, нет лока и конфликта). */
  enabled: boolean
}

/**
 * Автосохранение открытого блока: каждая пауза в правке шлёт `doSave` сама,
 * кнопка «Сохранить» не нужна. Правка во время летящего запроса не теряется —
 * `doSave` не затирает черновик ответом, если автор успел напечатать ещё, и
 * следующий цикл сохранит остаток. На смене блока недописанное отправляется сразу.
 */
export const useBlockAutoSave = ({ draft, block, isDirty, doSave, enabled }: Input) => {
  const saveRef = useRef(doSave)
  saveRef.current = doSave
  const timerRef = useRef<number | null>(null)
  const inFlightRef = useRef<Promise<void> | null>(null)

  const run = () => {
    timerRef.current = null
    // Запрос ещё летит — сохраним после него (черновик к тому времени свежий).
    if (inFlightRef.current) {
      void inFlightRef.current.then(() => {
        timerRef.current = window.setTimeout(run, 0)
      })
      return
    }
    const p = saveRef.current().finally(() => {
      inFlightRef.current = null
    })
    inFlightRef.current = p
  }

  // Настоящая правка — черновик расходится со СВОИМ серверным блоком. На кадре
  // смены выделения черновик ещё старый, а блок уже новый: это не правка.
  const isEdit = enabled && isDirty && draft !== null && block !== null && draft.id === block.id
  useEffect(() => {
    if (!isEdit) return
    timerRef.current = window.setTimeout(run, AUTO_SAVE_DELAY_MS)
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    // `run` читает всё через рефы; перезапуск — на каждую правку черновика.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, draft])

  // Ушли на другой блок (или закрыли панель), не дождавшись паузы, — дописываем
  // сразу. Рефы обновляются в эффекте и только на кадрах, где черновик и блок —
  // одно и то же: на кадре смены выделения `doSave` уже сравнивал бы старый
  // черновик с новым блоком, а признак правки был бы ложным.
  const pendingRef = useRef(false)
  const flushRef = useRef(doSave)
  const synced = draft !== null && block !== null && draft.id === block.id
  useEffect(() => {
    if (!synced) return
    pendingRef.current = isEdit
    flushRef.current = doSave
  })
  // Правка ещё не на сервере — браузер спрашивает, точно ли уходить. Иначе обновление страницы
  // сразу после загрузки картинки молча теряло её: в блоке она только в черновике, а запрос
  // в момент ухода браузер обрывает.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (pendingRef.current || inFlightRef.current) event.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [])

  const draftId = draft?.id ?? null
  useEffect(
    () => () => {
      if (!pendingRef.current) return
      pendingRef.current = false
      void flushRef.current()
    },
    [draftId],
  )
}
